import React from 'react';
import { MdOutlineFactCheck } from 'react-icons/md';
import { SectionCard, Pill, Chip, Note, FactGrid, StatTile, SubHeading, Disclosure } from './ui';
import { fmtNum, fmtPct, fmtDate, fmtDateTime, fmtDuration, fmtAcres, fmtSqft, titleCase, safeArray, isNum, DASH } from '../format';

/**
 * Site screening: factual parcel screens from public sources, one tile per layer.
 * Checked zero, not covered, not applicable, unavailable and timed out are shown
 * as different states in words, never only by color, and nothing here is a
 * score or a buildability conclusion.
 */

export const LAYER_LABELS = {
  epa_facilities: 'EPA facility registry',
  coastal_barrier: 'Coastal barrier units (CBRS)',
  flood_zone: 'FEMA flood zones',
  wildfire_hazard: 'Wildfire hazard potential',
  landslide_susceptibility: 'Landslide susceptibility',
};

const GROUP_LABELS = {
  water_and_flood: 'Water & flood',
  environmental_and_protected: 'Environmental & protected areas',
  site_conditions: 'Site conditions & access',
};

const SOURCE_LABELS = {
  epa_frs_facility_registry: 'EPA Facility Registry Service',
  usfws_cbrs_units: 'USFWS Coastal Barrier Resources System',
  fema_nfhl: 'FEMA National Flood Hazard Layer',
  usfs_wildfire_hazard_potential: 'USFS Wildfire Hazard Potential (270 m)',
  usgs_landslide_susceptibility_n10: 'USGS landslide susceptibility model (~90 m)',
};

const REASON_COPY = {
  not_covered: 'Not covered here',
  source_unavailable: 'Unavailable · source failed',
  timeout: 'Unavailable · timed out',
  invalid_geometry: 'Not run · no valid parcel boundary',
  insufficient_resolution: 'Insufficient resolution',
  not_applicable: 'Not applicable',
  not_loaded: 'Unavailable · not loaded',
  not_entitled: 'Not entitled',
  stale: 'Stale',
  ambiguous_match: 'Ambiguous match',
};

export function layerState(layer) {
  if (!layer) return { label: 'Not requested', tone: 'muted', kind: 'not_requested' };
  const { status, missing_reason: reason, data } = layer;
  if (status === 'ok' || status === 'partial') {
    if (data?.checked_zero) return { label: 'Checked · none found', tone: 'neutral', kind: 'checked_zero' };
    return { label: status === 'partial' ? 'Partial result' : 'Checked', tone: status === 'partial' ? 'warning' : 'positive', kind: status };
  }
  if (status === 'not_found') return { label: 'Checked · none found', tone: 'neutral', kind: 'checked_zero' };
  if (status === 'not_covered') return { label: REASON_COPY.not_covered, tone: 'muted', kind: 'not_covered' };
  if (status === 'not_requested') return { label: 'Not requested', tone: 'muted', kind: 'not_requested' };
  return { label: REASON_COPY[reason] || 'Unavailable', tone: 'critical', kind: 'unavailable' };
}

function Provenance({ layer }) {
  const p = layer?.provenance || {};
  const bits = [
    SOURCE_LABELS[p.source_id] || p.source_id,
    p.release_id ? `release ${p.release_id}` : null,
    p.storage?.mode === 'preloaded' ? 'preloaded' : p.storage?.mode === 'ephemeral' ? 'fetched live, not stored' : null,
    isNum(p.duration_ms) ? fmtDuration(p.duration_ms) : null,
    p.retrieved_at ? `retrieved ${fmtDateTime(p.retrieved_at)}` : null,
  ].filter(Boolean);
  return <div className="le-screen-provenance">{bits.join(' · ')}</div>;
}

function Limitations({ layer }) {
  const items = safeArray(layer?.limitations);
  if (!items.length) return null;
  return (
    <Disclosure summary={`${items.length} limitation${items.length === 1 ? '' : 's'}`}>
      <ul className="le-warning-list">{items.map((l) => <li key={l}>{l}</li>)}</ul>
    </Disclosure>
  );
}

function Facilities({ data }) {
  const counts = data?.counts || {};
  const nearest = safeArray(data?.nearest);
  const shown = nearest.slice(0, 8);
  return (
    <>
      <div className="le-stat-row le-stat-row-compact">
        <StatTile label="On the parcel" value={fmtNum(counts.on_parcel)} size="sm" />
        <StatTile label="Within ¼ mi" value={fmtNum(counts.within_quarter_mile)} size="sm" />
        <StatTile label="Within ½ mi" value={fmtNum(counts.within_half_mile)} size="sm" />
        <StatTile label={`Within ${data?.radius_miles ?? 1} mi`} value={fmtNum(counts.within_radius)} sub={counts.truncated ? `nearest ${fmtNum(counts.returned)} listed` : null} size="sm" />
      </div>
      {shown.length ? (
        <div className="le-table-wrap le-screen-table">
          <table className="le-table">
            <thead><tr><th>Facility</th><th>Type</th><th className="num">Distance</th><th>Programs</th></tr></thead>
            <tbody>
              {shown.map((s) => (
                <tr key={s.registry_id}>
                  <td><div className="le-screen-name">{s.name || s.registry_id}</div>{s.address ? <div className="muted le-screen-sub">{s.address}</div> : null}</td>
                  <td>{s.site_type ? titleCase(s.site_type) : DASH}</td>
                  <td className="num">{isNum(s.distance_ft) ? (s.on_parcel ? 'on parcel' : `${fmtNum(s.distance_ft)} ft`) : DASH}</td>
                  <td>{safeArray(s.programs).length ? safeArray(s.programs).join(', ') : DASH}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="muted">No registered facilities within {data?.radius_miles ?? 1} mi.</div>
      )}
      {nearest.length > shown.length ? (
        <Disclosure summary={`All ${nearest.length} listed facilities`}>
          <ul className="le-screen-list">
            {nearest.slice(shown.length).map((s) => (
              <li key={s.registry_id}>{s.name || s.registry_id}{s.site_type ? ` · ${titleCase(s.site_type)}` : ''}{isNum(s.distance_ft) ? ` · ${fmtNum(s.distance_ft)} ft` : ''}</li>
            ))}
          </ul>
        </Disclosure>
      ) : null}
    </>
  );
}

function CoastalBarrier({ data }) {
  const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);
  const units = safeArray(data?.intersecting_units);
  const prohibitions = safeArray(data?.intersecting_prohibition_areas);
  const nearest = safeArray(data?.nearest_units_within_5_miles);
  const buffers = safeArray(data?.buffer_zones_within_5_miles);
  return (
    <>
      <FactGrid
        columns={3}
        items={[
          { label: 'System unit', value: yesNo(data?.parcel_intersects_system_unit), hint: 'Parcel intersects a CBRS System Unit' },
          { label: 'Otherwise protected area', value: yesNo(data?.parcel_intersects_otherwise_protected_area) },
          { label: 'Prohibition area', value: yesNo(data?.parcel_intersects_prohibition_area) },
        ]}
      />
      {units.length || prohibitions.length ? (
        <ul className="le-screen-list">
          {units.map((u) => <li key={`u-${u.unit_id}`}><strong>{u.unit_id}</strong> {u.unit_name || ''} · {u.unit_type || u.feature_kind}{isNum(u.area_acres) ? ` · ${fmtNum(u.area_acres)} ac` : ''}</li>)}
          {prohibitions.map((u) => <li key={`p-${u.unit_id}`}><strong>{u.unit_id}</strong> {u.unit_name || ''} · prohibition area</li>)}
        </ul>
      ) : null}
      <div className="le-screen-facts">
        {nearest.length ? <span>Nearest unit: <strong>{nearest[0].unit_id}</strong>{nearest[0].unit_name ? ` ${nearest[0].unit_name}` : ''} · {fmtNum(nearest[0].distance_ft)} ft</span> : <span>No unit within 5 mi.</span>}
        {buffers.length ? <span> · {buffers.length} buffer zone{buffers.length === 1 ? '' : 's'} within 5 mi (nearest {fmtNum(buffers[0].distance_ft)} ft)</span> : null}
      </div>
    </>
  );
}

// Same blue ramp as Atlas's flood map layers (backend land_map_colors.json "flood"); darker = more restricted.
const FLOOD_CLASS_COLORS = {
  floodway: '#1e3a8a', high_risk: '#2563eb', moderate: '#93c5fd', minimal: '#d1d5db',
  undetermined: '#9ca3af', open_water: '#7dd3fc', not_included: '#e5e7eb', other: '#e5e7eb',
};
const SFHA_COPY = { entire_parcel: 'the whole lot', partial: 'part of the lot', none: 'none of the lot' };

/** Risk first, FEMA code in brackets: "Floodway (AE)", "1% zone (AE)", "0.2% zone (X)". The full FEMA label is the cell title. */
function floodZoneName(z) {
  const code = z.zone ? ` (${z.zone})` : '';
  const levee = String(z.subtype || z.label || '').toUpperCase().includes('LEVEE');
  switch (z.class) {
    case 'floodway': return `Floodway${code}`;
    case 'high_risk': return `1% zone${code}`;
    case 'moderate': return levee ? `Levee area${code}` : `0.2% zone${code}`;
    case 'minimal': return `Minimal${code}`;
    case 'undetermined': return `Undetermined${code}`;
    case 'open_water': return 'Open water';
    case 'not_included': return 'Not studied by FEMA';
    default: return z.label || z.zone || DASH;
  }
}

function pctOfLot(share) {
  if (!isNum(share)) return DASH;
  const pct = share * 100;
  return fmtPct(pct, pct > 0 && pct < 10 ? 1 : 0);
}

function FloodZone({ data }) {
  const shares = data?.shares || {};
  const zones = safeArray(data?.share_of_parcel_by_zone);
  const bfe = data?.bfe_ft;
  const panels = safeArray(data?.firm_panels);
  const lomrs = safeArray(data?.lomrs);
  const bldg = data?.buildings;
  const items = safeArray(bldg?.items);
  const touching = items.filter((b) => (b.share_in_high_risk || 0) > 0.001);
  const distances = items.map((b) => b.distance_to_high_risk_ft).filter(isNum);
  const nearestBuildingFt = distances.length ? Math.min(...distances) : null;
  const floodway = shares.floodway || 0;
  const ft = (v) => fmtNum(v, Number.isInteger(v) ? 0 : 1);
  const bfeValue = bfe ? (bfe.min === bfe.max ? `${ft(bfe.min)} ft` : `${ft(bfe.min)}–${ft(bfe.max)} ft`) : DASH;
  const bfeSub = bfe
    ? [bfe.datum, bfe.source === 'static_bfe_of_zone' ? 'published for the zone' : 'lines crossing the lot'].filter(Boolean).join(' · ')
    : (data?.sfha ? 'no base flood elevation published here' : 'no 1% annual chance zone on the lot');
  const panelSub = panels.length ? `${panels.slice(0, 2).map((pn) => pn.firm_pan).join(', ')}${panels.length > 2 ? ` +${panels.length - 2} more` : ''}` : null;
  let buildingsSub = 'no building outlines on the lot';
  if (bldg) {
    if (bldg.touching_floodway) buildingsSub = `${fmtNum(bldg.touching_floodway)} in the floodway`;
    else if (!bldg.touching_high_risk && isNum(nearestBuildingFt)) buildingsSub = `nearest is ${fmtNum(nearestBuildingFt)} ft from the zone`;
    else buildingsSub = null;
  }
  return (
    <>
      <div className="le-stat-row le-stat-row-compact">
        <StatTile label="In the 1% annual chance zone" value={`${pctOfLot(shares.sfha_total)} of the lot`}
          sub={floodway > 0 ? `${pctOfLot(floodway)} is regulatory floodway` : SFHA_COPY[data?.sfha_status] || null} size="sm" emphasis />
        <StatTile label="Base flood elevation" value={bfeValue} sub={bfeSub} size="sm" />
        <StatTile label="FEMA map effective" value={data?.panel_effective ? fmtDate(data.panel_effective) : DASH} sub={panelSub} size="sm" />
        <StatTile label="Buildings touching the 1% zone" value={bldg ? `${fmtNum(bldg.touching_high_risk)} of ${fmtNum(bldg.on_parcel)}` : DASH} sub={buildingsSub} size="sm" />
      </div>
      {zones.length ? (
        <div className="le-table-wrap le-screen-table">
          <table className="le-table" aria-label="Share of the lot by FEMA flood zone">
            <thead><tr><th>Flood zone</th><th className="num">Share of lot</th><th className="num">Acres</th><th>1% zone</th></tr></thead>
            <tbody>
              {zones.map((z) => (
                <tr key={`${z.label}-${z.class}`}>
                  <td title={z.label}>
                    <span className="le-flood-swatch" style={{ background: FLOOD_CLASS_COLORS[z.class] || FLOOD_CLASS_COLORS.other }} aria-hidden="true" />
                    {floodZoneName(z)}
                  </td>
                  <td className="num">{pctOfLot(z.share)}</td>
                  <td className="num">{isNum(z.acres) ? fmtAcres(z.acres) : DASH}</td>
                  <td>{z.sfha ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {touching.length ? (
        <ul className="le-screen-list" aria-label="Buildings touching the 1% annual chance zone">
          {touching.map((b, i) => (
            <li key={`b-${i}`}>
              <strong>{fmtSqft(b.area_sqft)}</strong> building · {pctOfLot(b.share_in_high_risk)} in the 1% zone
              {(b.share_in_floodway || 0) > 0.001 ? ` (${pctOfLot(b.share_in_floodway)} in the floodway)` : ''}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="le-screen-facts">
        {data?.near_boundary ? <Chip>Near a zone edge: an elevation certificate decides, not the map line</Chip> : null}
        {data?.nearest_high_risk ? <Chip>Nearest 1% zone ({data.nearest_high_risk.zone}) {fmtNum(data.nearest_high_risk.distance_ft)} ft away</Chip> : null}
        {data?.approximate_study ? <Chip>Zone A: approximate study, no base flood elevation</Chip> : null}
        {data?.coastal_high_hazard ? <Chip>Coastal high hazard (V zone)</Chip> : null}
        {lomrs.length ? <Chip>{lomrs.length} Letter{lomrs.length === 1 ? '' : 's'} of Map Revision</Chip> : null}
      </div>
    </>
  );
}

function Wildfire({ data }) {
  const dist = safeArray(data?.class_distribution);
  const total = dist.reduce((s, d) => s + (d.cells || 0), 0);
  return (
    <>
      <div className="le-stat-row le-stat-row-compact">
        <StatTile label="At the parcel point" value={data?.class_label_at_point ? titleCase(data.class_label_at_point) : DASH} sub={isNum(data?.class_at_point) ? `class ${data.class_at_point} of 5` : null} size="sm" emphasis />
        <StatTile label="Highest class on parcel" value={data?.max_hazard_class_label ? titleCase(data.max_hazard_class_label) : DASH} sub={isNum(data?.parcel_cells) && data.parcel_cells > 0 ? `${fmtNum(data.parcel_cells)} cell${data.parcel_cells === 1 ? '' : 's'} at ${fmtNum(data.pixel_size_m)} m` : 'parcel smaller than one cell'} size="sm" />
      </div>
      {dist.length > 1 ? (
        <div className="le-screen-bars" role="list" aria-label="Wildfire hazard class share across the parcel">
          {dist.map((d) => (
            <div key={d.class} className="le-screen-bar-row" role="listitem">
              <span className="le-hbar-label">{d.label ? titleCase(d.label) : `Class ${d.class}`}</span>
              <span className="le-meter-track"><span className="le-meter-fill" style={{ width: `${Math.round((d.cells / Math.max(total, 1)) * 100)}%` }} /></span>
              <span className="le-hbar-value">{fmtPct(d.share * 100, 0)}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div className="le-screen-facts"><Chip>{data?.sample_method === 'parcel_cell_centers' ? 'Cells whose centres fall inside the parcel' : 'Point sample at the representative point'}</Chip></div>
    </>
  );
}

function Landslide({ data }) {
  const hasCells = isNum(data?.parcel_cells) && data.parcel_cells > 0;
  return (
    <>
      <div className="le-stat-row le-stat-row-compact">
        <StatTile label="At the parcel point" value={isNum(data?.value_at_point) ? `${data.value_at_point} / 81` : DASH} sub={isNum(data?.susceptible_share_at_point) ? `${fmtPct(data.susceptible_share_at_point * 100, 0)} of 10 m cells susceptible` : null} size="sm" emphasis />
        <StatTile label="Across the parcel" value={hasCells ? `${data.value_min}–${data.value_max}` : DASH} sub={hasCells ? `${fmtNum(data.parcel_cells)} cells · mean ${fmtNum(data.value_mean, 1)}` : 'parcel smaller than one cell'} size="sm" />
      </div>
      <div className="le-screen-facts"><Chip>{data?.sample_method === 'parcel_cell_centers' ? 'Cells whose centres fall inside the parcel' : 'Point sample at the representative point'}</Chip> <span className="muted">{data?.interpretation}</span></div>
    </>
  );
}

const RENDERERS = { epa_facilities: Facilities, coastal_barrier: CoastalBarrier, flood_zone: FloodZone, wildfire_hazard: Wildfire, landslide_susceptibility: Landslide };

function LayerTile({ layerKey, layer }) {
  const state = layerState(layer);
  const Body = RENDERERS[layerKey];
  const warnings = safeArray(layer?.warnings);
  return (
    <section className={`le-screen-layer le-screen-layer-${state.kind}`} aria-label={LAYER_LABELS[layerKey] || layerKey}>
      <header className="le-screen-layer-head">
        <h5>{LAYER_LABELS[layerKey] || titleCase(layerKey)}</h5>
        <Pill tone={state.tone}>{state.label}</Pill>
      </header>
      {layer?.data && Body ? <Body data={layer.data} /> : (
        <div className="muted le-screen-missing">
          {state.kind === 'not_requested' ? 'This layer was not requested.' : warnings.length ? warnings.join(' ') : 'No result for this layer.'}
        </div>
      )}
      {layer ? <Limitations layer={layer} /> : null}
      {layer ? <Provenance layer={layer} /> : null}
    </section>
  );
}

export default function SiteScreeningCard({ data, status }) {
  const layers = data?.layers || {};
  const groups = data?.layer_groups || {};
  const summary = data?.summary || {};
  const counts = summary.status_counts || {};
  const completed = (counts.ok || 0) + (counts.partial || 0) + (counts.not_found || 0) + (counts.not_covered || 0);
  const requested = isNum(summary.layers_requested) ? summary.layers_requested : Object.keys(layers).length;
  const facilities = layers.epa_facilities?.data?.counts;
  const wildfire = layers.wildfire_hazard?.data;
  const landslide = layers.landslide_susceptibility?.data;
  const flood = layers.flood_zone;
  const subtitle = data ? 'Factual parcel screens from public sources · checked zero, not covered and unavailable are reported separately · not a Phase I assessment, survey or flood determination' : null;

  return (
    <SectionCard id="le-section-screening" icon={<MdOutlineFactCheck size={20} />} title="Site screening" subtitle={subtitle} status={status} dataset={data}>
      {data ? (
        <>
          <div className="le-stat-row">
            <StatTile label="Layers completed" value={`${completed} of ${requested}`} sub={counts.not_covered ? `${counts.not_covered} not covered here` : null} emphasis />
            <StatTile label="Registered facilities within ½ mi" value={facilities ? fmtNum(facilities.within_half_mile) : DASH} sub={facilities ? `${fmtNum(facilities.within_radius)} within ${layers.epa_facilities?.data?.radius_miles ?? 1} mi` : null} />
            {flood ? (
              <StatTile label="FEMA 1% annual chance zone" value={flood.data ? `${pctOfLot(flood.data.shares?.sfha_total)} of the lot` : DASH}
                sub={flood.data ? (flood.data.shares?.floodway ? `${pctOfLot(flood.data.shares.floodway)} floodway` : null) : layerState(flood).label} />
            ) : null}
            <StatTile label="Wildfire hazard at point" value={wildfire?.class_label_at_point ? titleCase(wildfire.class_label_at_point) : DASH} sub={wildfire ? (wildfire.sample_method === 'parcel_cell_centers' ? 'parcel cells' : 'point sample') : null} />
            <StatTile label="Landslide susceptibility" value={isNum(landslide?.value_at_point) ? `${landslide.value_at_point} / 81` : DASH} sub={landslide ? 'susceptible 10 m cells per 90 m cell' : null} />
          </div>
          {data.parcel_ref?.kind === 'point_box' ? (
            <Note tone="warning"><strong>No parcel boundary.</strong> Every screen ran as a point screen around the subject; polygon results are not parcel-wide.</Note>
          ) : null}
          {safeArray(data.notes).length ? <Note tone="info"><ul className="le-warning-list">{data.notes.map((n) => <li key={n}>{n}</li>)}</ul></Note> : null}
          {Object.entries(groups).map(([groupKey, keys]) => {
            const present = safeArray(keys).filter((k) => k in layers);
            if (!present.length) return null;
            return (
              <div key={groupKey} className="le-screen-group">
                <SubHeading>{GROUP_LABELS[groupKey] || titleCase(groupKey)}</SubHeading>
                <div className="le-screen-grid">
                  {present.map((k) => <LayerTile key={k} layerKey={k} layer={layers[k]} />)}
                </div>
              </div>
            );
          })}
          {safeArray(data.limitations).length ? (
            <Disclosure summary="What this screening is and is not">
              <ul className="le-warning-list">{data.limitations.map((l) => <li key={l}>{l}</li>)}</ul>
            </Disclosure>
          ) : null}
          <div className="le-footnote">
            Parcel boundary from the zoning record{data.parcel_ref?.boundary_hash ? ` · ${data.parcel_ref.boundary_hash.slice(0, 19)}…` : ''} · method {data.method_version} · evaluated {fmtDateTime(data.evaluated_at)}. Registry presence, hazard classes and susceptibility values are facts from the named public sources, not conclusions about this site.
          </div>
        </>
      ) : null}
    </SectionCard>
  );
}
